import { apiGet, apiPost, apiPatch, apiDelete } from "../lib/api-client";
import type { StoreItemMetadata, StoreItemRarity } from "./store";

// ─── Types (mirror the 05-social serialized shapes — do not invent fields) ────
// See specs/05-social.md + the implementation contract's "Post serialized shape".

export type PostKind = "text" | "achievement" | "activity";
export type PostVisibility = "public" | "private" | "group";
export type AchievementTier = "bronze" | "silver" | "gold" | "platinum" | "mythic";

export interface EquippedItem {
  itemId: string;
  slug: string;
  name: string;
  metadata: StoreItemMetadata;
  rarity: StoreItemRarity;
}

export interface PostAuthor {
  username: string;
  name: string | null;
  avatarUrl: string | null;
  /** Link to /explorers/:username only when not false (absent on older API builds). */
  profilePublic?: boolean;
  title: EquippedItem | null;
  badges: EquippedItem[];
  banner: EquippedItem | null;
}

export interface PostAchievementEmbed {
  slug: string;
  name: string;
  tier: AchievementTier;
  points: number;
  iconName: string | null;
  unlockedAt: string;
}

export interface PostActivityEmbed {
  name: string;
  startedAt: string;
  distanceM: number | null;
  elevationGainM: number | null;
  summitCount: number;
  /** Normalized 0..1 (y up), privacy-safe — see ActivityTraceSVG. */
  tracePoints: Array<[number, number]>;
}

export interface PostPhoto {
  url: string;
  width: number | null;
  height: number | null;
}

export interface PostAward {
  itemSlug: string;
  name: string;
  icon: string | null;
  count: number;
}

export interface PostViewer {
  canDelete: boolean;
  liked: boolean;
  awardedItemSlugs: string[];
}

export interface SocialPost {
  id: string;
  kind: PostKind;
  body: string | null;
  visibility: PostVisibility;
  /** Set when visibility is "group" (spec 32): only the group's members see it. */
  groupId?: string | null;
  createdAt: string;
  author: PostAuthor;
  achievement: PostAchievementEmbed | null;
  activity: PostActivityEmbed | null;
  photos: PostPhoto[];
  commentCount: number;
  likeCount: number;
  awardPointsTotal: number;
  awards: PostAward[];
  viewer: PostViewer;
}

export type FeedTab = "popular" | "following";

// ─── Recent check-ins (community grid tab) ────────────────────────────────────

export interface RecentCheckinAuthor {
  username: string | null;
  name: string | null;
  avatarUrl: string | null;
  banner: EquippedItem | null;
  /** false → handle-only attribution, no /explorers link. */
  profilePublic: boolean;
}

export interface RecentCheckinLandmark {
  slug: string;
  name: string;
  category: string;
  region: string | null;
  country: string | null;
  /** "peak" = a summit check-in: slug and name are the peak's (spec 34). */
  itemType?: "landmark" | "peak";
}

export interface RecentCheckin {
  id: string;
  photoUrl: string;
  width: number | null;
  height: number | null;
  caption: string | null;
  checkedInAt: string;
  createdAt: string;
  author: RecentCheckinAuthor;
  landmark: RecentCheckinLandmark;
}

export interface RecentCheckinsResponse {
  items: RecentCheckin[];
  nextCursor: string | null;
}

export interface FeedResponse {
  items: SocialPost[];
  nextCursor: string | null;
  /** Resolved tab — only on GET /social/feed (absent on explorer posts). When
   * the request omitted ?tab, this is the server-chosen default: following if
   * the viewer follows anyone, else popular. */
  tab?: FeedTab;
  /** How many explorers the viewer follows — only on GET /social/feed. */
  followingCount?: number;
}

export interface CreatePostInput {
  kind: PostKind;
  body?: string;
  achievementId?: string;
  activityId?: string;
  photos?: Array<{ key: string; width?: number; height?: number }>;
  visibility?: PostVisibility;
  /** Required with visibility "group" (spec 32). */
  groupId?: string;
  /** Idempotency key (8-64 chars, [A-Za-z0-9_-]): a retry with the same key
   * gets the first post back (200) rather than posting twice. */
  clientKey?: string;
}

export interface PostCommentAuthor {
  // Nullable: the API only gates *posting* on having claimed a username, not
  // commenting — users.username is nullable, so a commenter may not have one.
  username: string | null;
  name: string | null;
  avatarUrl: string | null;
}

export interface PostComment {
  id: string;
  body: string;
  createdAt: string;
  author: PostCommentAuthor;
  viewer: { canDelete: boolean };
}

export interface CommentsResponse {
  items: PostComment[];
  nextCursor: string | null;
}

export interface NotificationItem {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsResponse {
  items: NotificationItem[];
  nextCursor: string | null;
  unreadCount: number;
}

export interface ExplorerRecentUnlock {
  achievementId: string;
  slug: string;
  name: string;
  description: string;
  tier: AchievementTier;
  points: number;
  iconName: string | null;
  unlockedAt: string;
}

export interface ExplorerCustomCollection {
  id: string;
  name: string;
  itemCount: number;
  completedCount: number;
  pct: number;
}

export interface ExplorerProfile {
  username: string;
  name: string | null;
  avatarUrl: string | null;
  level: number;
  outdoorScore: number;
  stats: { totalSummits: number; totalDistanceM: number; totalDaysOut: number };
  equipped: {
    badges: EquippedItem[];
    banner: EquippedItem | null;
    title: EquippedItem | null;
    frame: EquippedItem | null;
  };
  recentUnlocks: ExplorerRecentUnlock[];
  customCollections: ExplorerCustomCollection[];
  followerCount: number;
  followingCount: number;
  viewer: { isSelf: boolean; isFollowing: boolean };
}

export interface ExplorerResponse {
  explorer: ExplorerProfile;
}

// ─── Fetch functions ──────────────────────────────────────────────────────────

function withCursor(path: string, cursor?: string): string {
  return cursor ? `${path}?cursor=${encodeURIComponent(cursor)}` : path;
}

export function fetchFeed(cursor?: string, tab?: FeedTab): Promise<FeedResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (tab) params.set("tab", tab);
  const qs = params.toString();
  return apiGet(qs ? `/social/feed?${qs}` : "/social/feed");
}

export function fetchRecentCheckins(cursor?: string): Promise<RecentCheckinsResponse> {
  return apiGet(withCursor("/social/checkins", cursor));
}

export function followExplorer(username: string): Promise<{ ok: boolean; following: boolean }> {
  return apiPost("/social/follows", { username });
}

export function unfollowExplorer(username: string): Promise<{ ok: boolean; following: boolean }> {
  return apiDelete(`/social/follows/${encodeURIComponent(username)}`);
}

export function createPost(input: CreatePostInput): Promise<{ post: SocialPost }> {
  return apiPost("/social/posts", input);
}

export function deletePost(id: string): Promise<{ ok: boolean }> {
  return apiDelete(`/social/posts/${id}`);
}

export function fetchComments(postId: string, cursor?: string): Promise<CommentsResponse> {
  return apiGet(withCursor(`/social/posts/${postId}/comments`, cursor));
}

export function addComment(postId: string, body: string): Promise<{ comment: PostComment }> {
  return apiPost(`/social/posts/${postId}/comments`, { body });
}

export function likePost(postId: string): Promise<{ ok: boolean; liked: boolean; likeCount: number }> {
  return apiPost(`/social/posts/${postId}/like`, {});
}

export function unlikePost(postId: string): Promise<{ ok: boolean; liked: boolean; likeCount: number }> {
  return apiDelete(`/social/posts/${postId}/like`);
}

export interface PostLiker {
  username: string | null;
  name: string | null;
  avatarUrl: string | null;
  likedAt: string;
}

/** Post author only — 404 for anyone else. */
export function fetchPostLikers(postId: string): Promise<{ likers: PostLiker[] }> {
  return apiGet(`/social/posts/${postId}/likes`);
}

export function deleteComment(id: string): Promise<{ ok: boolean }> {
  return apiDelete(`/social/comments/${id}`);
}

export function giveAward(
  postId: string,
  itemSlug: string,
): Promise<{ ok: boolean; awards: PostAward[]; storeLink?: string }> {
  return apiPost(`/social/posts/${postId}/award`, { itemSlug });
}

export function reportPost(postId: string, reason: string): Promise<{ ok: boolean }> {
  return apiPost(`/social/posts/${postId}/report`, { reason });
}

export function getPhotoUploadUrl(
  contentType: string,
): Promise<{ uploadUrl: string; key: string; publicUrl: string }> {
  return apiPost("/social/photos/upload-url", { contentType });
}

export function fetchExplorer(username: string): Promise<ExplorerResponse> {
  return apiGet(`/explorers/${encodeURIComponent(username)}`);
}

export function fetchExplorerPosts(username: string, cursor?: string): Promise<FeedResponse> {
  return apiGet(withCursor(`/explorers/${encodeURIComponent(username)}/posts`, cursor));
}

// Public, read-only custom-list detail (PLT-7) — mirrors UserCollectionDetail's
// shape from api/user-collections.ts but has no auth/ownership actions.
export interface ExplorerCollectionItem {
  id: string;
  itemType: "peak" | "landmark" | "trail";
  name: string;
  slug: string;
  collected: boolean;
  addedAt: string;
  elevationM: number | null;
  category: string | null;
  distanceKm: number | null;
  totalSections: number | null;
  completedSections: number | null;
}

export interface ExplorerCollectionResponse {
  collection: { id: string; name: string; description: string | null; createdAt: string };
  items: ExplorerCollectionItem[];
}

export function fetchExplorerCollection(username: string, id: string): Promise<ExplorerCollectionResponse> {
  return apiGet(`/explorers/${encodeURIComponent(username)}/collections/${id}`);
}

export function fetchNotifications(cursor?: string): Promise<NotificationsResponse> {
  return apiGet(withCursor("/notifications", cursor));
}

export function markNotificationsRead(ids?: string[]): Promise<{ ok: boolean }> {
  return apiPost("/notifications/read", ids ? { ids } : {});
}

// ─── Account patch (username claim / profile visibility / social prefs) ───────
// No dedicated api/account.ts exists yet — ProfilePage.tsx calls apiPatch("/account/me", ...)
// inline. This thin wrapper is used by the FeedPage username-claim prompt (and is reusable by
// ProfilePage's "Public profile" panel later).

export interface AccountPatchInput {
  username?: string;
  profileVisibility?: "public" | "private";
  socialPreferences?: { autoPostAchievements?: boolean };
}

export function patchAccount(data: AccountPatchInput): Promise<{ user: unknown }> {
  return apiPatch("/account/me", data);
}

// ─── Relative time (mono, used by PostCard / NotificationsBell) ───────────────

const RELATIVE_UNITS: [number, string][] = [
  [60, "s"],
  [60, "m"],
  [24, "h"],
  [7, "d"],
  [4.345, "w"],
  [12, "mo"],
  [Number.POSITIVE_INFINITY, "y"],
];

export function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  let diff = Math.max(0, (Date.now() - then) / 1000);
  if (diff < 5) return "now";
  for (const [amount, unit] of RELATIVE_UNITS) {
    if (diff < amount) return `${Math.floor(diff)}${unit}`;
    diff /= amount;
  }
  return `${Math.floor(diff)}y`;
}
